import { MemberWithUser } from '@zond/api-interfaces';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
} from '@zond/ui';
import { Search, UserPlus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMembers } from '../../../members/members.queries';

interface ProjectMembersListProps {
  projectId: number;
  selectedUserIds: number[];
  onUserIdsChange: (userIds: number[]) => void;
}

export function ProjectMembersList({ projectId, selectedUserIds, onUserIdsChange }: ProjectMembersListProps) {
  const { t } = useTranslation('projects');
  const { data: members, isLoading } = useGetMembers();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const acceptedMembers = (members?.filter((member) => member.status === 'Active') || []) as MemberWithUser[];

  const currentMembers = acceptedMembers.filter(
    (member) => member.userId && selectedUserIds.includes(member.userId)
  );

  const availableMembers = acceptedMembers.filter(
    (member) => member.userId && !selectedUserIds.includes(member.userId)
  );

  const filteredAvailableMembers = availableMembers.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    const user = member.user as any;
    const email = user?.email?.toLowerCase() || '';
    const displayName = user?.displayname?.toLowerCase() || '';
    const fullName = `${user?.firstname || ''} ${user?.lastname || ''}`.toLowerCase();

    return email.includes(searchLower) || displayName.includes(searchLower) || fullName.includes(searchLower);
  });

  const addUser = (userId: number) => {
    onUserIdsChange([...selectedUserIds, userId]);
    setSearchQuery('');
  };

  const removeUser = (userId: number) => {
    onUserIdsChange(selectedUserIds.filter((id) => id !== userId));
  };

  const getInitials = (user: any) => {
    if (user?.displayname) {
      return user.displayname.slice(0, 2).toUpperCase();
    }
    const first = user?.firstname?.[0] || '';
    const last = user?.lastname?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getUserDisplayName = (user: any) => {
    if (user?.displayname) return user.displayname;
    if (user?.firstname || user?.lastname) {
      return `${user?.firstname || ''} ${user?.lastname || ''}`.trim();
    }
    return user?.email || t('members.unknownUser');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('members.teamTitle')}</CardTitle>
          <CardDescription>{t('members.loading')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">
              {t('members.title', { count: currentMembers.length })}
            </CardTitle>
            <CardDescription className="text-xs">{t('members.subtitle')}</CardDescription>
          </div>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" disabled={availableMembers.length === 0}>
                <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                {t('common:actions.add')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[280px] p-0">
              <div className="p-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder={t('form.searchMembers')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {filteredAvailableMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">{t('members.noMatches')}</p>
                ) : (
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {filteredAvailableMembers.map((member) => (
                      <div
                        key={member.userId}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer transition-colors"
                        onClick={() => {
                          if (member.userId) {
                            addUser(member.userId);
                          }
                        }}
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={member.user?.avatar ?? undefined} />
                          <AvatarFallback className="text-xs">
                            {member.user?.displayname?.[0] ?? '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{getUserDisplayName(member.user)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Current Members */}
        {currentMembers.length > 0 ? (
          <div className="space-y-1.5">
            {currentMembers.map((member) => (
              <div
                key={member.userId}
                className="flex items-center gap-2 p-2 rounded-md border hover:bg-accent/50 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member.user?.avatar ?? undefined} />
                  <AvatarFallback>{member.user?.displayname?.[0] ?? '?'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{getUserDisplayName(member.user)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => member.userId && removeUser(member.userId)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>{t('members.empty')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
